import React from 'react';
import { Check, Minus } from 'lucide-react';

/**
 * Grouped permission checkbox matrix.
 * @param {Array} allPermissions - from GET /permissions
 * @param {Set} selected - currently selected permission keys
 * @param {Function} onChange - called with new Set
 * @param {boolean} disabled
 * @param {string} disabledTooltip
 * @param {Object} overrideIndicators - optional map { key: 'grant'|'revoke' } for override badges
 */
export default function PermissionMatrix({ allPermissions, selected, onChange, disabled, disabledTooltip, overrideIndicators }) {
  const grouped = {};
  allPermissions.forEach(p => {
    if (!grouped[p.module]) grouped[p.module] = [];
    grouped[p.module].push(p);
  });
  const modules = Object.keys(grouped).sort();

  const togglePerm = (key) => {
    if (disabled) return;
    const next = new Set(selected);
    if (next.has(key)) next.delete(key); else next.add(key);
    onChange(next);
  };

  const toggleModule = (mod) => {
    if (disabled) return;
    const keys = grouped[mod].map(p => p.key);
    const allChecked = keys.every(k => selected.has(k));
    const next = new Set(selected);
    keys.forEach(k => allChecked ? next.delete(k) : next.add(k));
    onChange(next);
  };

  return (
    <div className="space-y-3" data-testid="permission-matrix">
      {modules.map(mod => {
        const perms = grouped[mod];
        const checkedCount = perms.filter(p => selected.has(p.key)).length;
        const allChecked = checkedCount === perms.length;
        const someChecked = checkedCount > 0 && !allChecked;
        return (
          <div key={mod} className="border border-gray-200 rounded-lg overflow-hidden">
            <button
              type="button"
              onClick={() => toggleModule(mod)}
              disabled={disabled}
              className="w-full flex items-center gap-3 px-4 py-2.5 bg-gray-50 hover:bg-gray-100 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              data-testid={`perm-module-${mod}`}
              title={disabled ? disabledTooltip : undefined}
            >
              <ModuleCheckbox allChecked={allChecked} someChecked={someChecked} />
              <span className="text-sm font-bold text-gray-900 capitalize">{mod.replace(/_/g, ' ')}</span>
              <span className="text-[11px] text-gray-400 ml-auto">{checkedCount}/{perms.length}</span>
            </button>
            <div className="px-4 py-1.5 space-y-0.5">
              {perms.map(p => {
                const checked = selected.has(p.key);
                const indicator = overrideIndicators?.[p.key];
                return (
                  <label
                    key={p.key}
                    data-testid={`perm-check-${p.key}`}
                    title={disabled ? disabledTooltip : p.key}
                    className={`flex items-center gap-3 py-1.5 rounded px-1 -mx-1 transition-colors ${disabled ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer hover:bg-gray-50'}`}
                  >
                    <input type="checkbox" className="sr-only" checked={checked} onChange={() => togglePerm(p.key)} disabled={disabled} />
                    <div className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 transition-colors ${
                      checked ? 'bg-brand-magenta border-brand-magenta' : 'border-gray-300 bg-white'
                    }`}>
                      {checked && <Check className="w-2.5 h-2.5 text-white" />}
                    </div>
                    <span className="text-sm text-gray-700 flex-1">{p.description || p.key}</span>
                    {indicator === 'grant' && <span className="text-[9px] font-black text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded">+GRANT</span>}
                    {indicator === 'revoke' && <span className="text-[9px] font-black text-red-600 bg-red-50 px-1.5 py-0.5 rounded">−REVOKE</span>}
                  </label>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function ModuleCheckbox({ allChecked, someChecked }) {
  return (
    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
      allChecked ? 'bg-brand-magenta border-brand-magenta' :
      someChecked ? 'bg-brand-magenta/40 border-brand-magenta' :
      'border-gray-300 bg-white'
    }`}>
      {allChecked ? <Check className="w-3 h-3 text-white" /> :
       someChecked ? <Minus className="w-3 h-3 text-white" /> : null}
    </div>
  );
}
